/**
 * Monaco-backed YAML editor used by the feed wizard mapping pane.
 *
 * Design notes:
 *   - Theme tokens come from getComputedStyle(:root) so the editor tracks
 *     both dataflowx/ember palettes + light/dark without hard-coded hex.
 *   - Emits debounced (default 250ms) value changes so preview + YAML-parse
 *     don't thrash.
 *   - ⌘S (or Ctrl-S) calls onSave if provided.
 *   - Respects prefers-reduced-motion: cursor/scroll smoothness off.
 */

import { useCallback, useEffect, useRef } from "react";
import Editor, { type Monaco, type OnMount } from "@monaco-editor/react";

interface MappingEditorProps {
    value: string;
    onChange: (v: string) => void;
    onSave?: () => void;
    readOnly?: boolean;
    debounceMs?: number;
    height?: string | number;
    /** Override for the editor language (default "yaml"). */
    language?: string;
}

function getVar(name: string, fallback: string): string {
    if (typeof window === "undefined") return fallback;
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
}

function cssColorToHex(c: string): string {
    // Monaco's defineTheme requires hex-only colors. Accept tokens that evaluate
    // to #rgb, #rrggbb, or rgb()/rgba() — normalize to #rrggbb.
    if (!c) return "#000000";
    c = c.trim();
    if (c.startsWith("#")) {
        if (c.length === 4) {
            // #rgb → #rrggbb
            return "#" + c.slice(1).split("").map((ch) => ch + ch).join("");
        }
        if (c.length === 7) return c.toLowerCase();
        if (c.length === 9) return c.slice(0, 7).toLowerCase(); // strip alpha
    }
    const m = c.match(/rgba?\(([^)]+)\)/);
    if (m) {
        const parts = m[1].split(",").map((s) => s.trim());
        const r = Math.round(Number(parts[0] || 0));
        const g = Math.round(Number(parts[1] || 0));
        const b = Math.round(Number(parts[2] || 0));
        const hex = (n: number) => n.toString(16).padStart(2, "0");
        return "#" + hex(r) + hex(g) + hex(b);
    }
    return "#000000";
}

function defineThemes(monaco: Monaco): void {
    const bg        = cssColorToHex(getVar("--color-surface-raised", "#ffffff"));
    const fg        = cssColorToHex(getVar("--color-text",           "#111111"));
    const muted     = cssColorToHex(getVar("--color-text-muted",     "#666666"));
    const subtle    = cssColorToHex(getVar("--color-text-subtle",    "#999999"));
    const accent    = cssColorToHex(getVar("--color-accent",         "#3367ff"));
    const success   = cssColorToHex(getVar("--color-success",        "#16a34a"));
    const warn      = cssColorToHex(getVar("--color-warn",           "#f59e0b"));
    const danger    = cssColorToHex(getVar("--color-danger",         "#dc2626"));
    const border    = cssColorToHex(getVar("--color-border",         "#e5e7eb"));
    const selection = cssColorToHex(getVar("--color-accent-soft",    "#dbe3ff"));

    const isDark = document.documentElement.getAttribute("data-mode") === "dark";

    monaco.editor.defineTheme("intelroom", {
        base: isDark ? "vs-dark" : "vs",
        inherit: true,
        rules: [
            { token: "comment",      foreground: subtle.slice(1), fontStyle: "italic" },
            { token: "string",       foreground: success.slice(1) },
            { token: "number",       foreground: warn.slice(1) },
            { token: "keyword",      foreground: accent.slice(1) },
            { token: "type",         foreground: accent.slice(1) },
            { token: "delimiter",    foreground: muted.slice(1) },
            { token: "key",          foreground: accent.slice(1) },
            { token: "invalid",      foreground: danger.slice(1) },
        ],
        colors: {
            "editor.background":              bg,
            "editor.foreground":              fg,
            "editorLineNumber.foreground":    subtle,
            "editorLineNumber.activeForeground": muted,
            "editor.selectionBackground":     selection,
            "editor.inactiveSelectionBackground": selection,
            "editorCursor.foreground":        accent,
            "editor.lineHighlightBackground": bg,
            "editorIndentGuide.background":   border,
            "editorIndentGuide.activeBackground": muted,
            "editorBracketMatch.background":  selection,
            "editorBracketMatch.border":      accent,
            "editorWidget.background":        bg,
            "editorWidget.border":            border,
        },
    });
}

export function MappingEditor(props: MappingEditorProps): React.ReactElement {
    const {
        value,
        onChange,
        onSave,
        readOnly = false,
        debounceMs = 250,
        height = 360,
        language = "yaml",
    } = props;

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestRef = useRef(value);
    const monacoRef = useRef<Monaco | null>(null);

    const handleMount: OnMount = useCallback(
        (editor, monaco) => {
            monacoRef.current = monaco;
            defineThemes(monaco);
            monaco.editor.setTheme("intelroom");

            if (onSave) {
                editor.addCommand(
                    monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                    () => onSave(),
                );
            }

            // Re-apply theme when the user toggles mode.
            const mo = new MutationObserver(() => {
                defineThemes(monaco);
                monaco.editor.setTheme("intelroom");
            });
            mo.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ["data-mode"],
            });
        },
        [onSave],
    );

    useEffect(() => {
        latestRef.current = value;
    }, [value]);

    const handleChange = useCallback(
        (v: string | undefined) => {
            const next = v ?? "";
            latestRef.current = next;
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => onChange(latestRef.current), debounceMs);
        },
        [onChange, debounceMs],
    );

    useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        [],
    );

    const reduced =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    return (
        <div
            style={{
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                overflow: "hidden",
                background: "var(--color-surface-raised)",
            }}
        >
            <Editor
                height={height}
                language={language}
                value={value}
                onChange={handleChange}
                onMount={handleMount}
                theme="intelroom"
                options={{
                    readOnly,
                    minimap: { enabled: false },
                    fontSize: 13,
                    fontFamily: "var(--font-family-mono), ui-monospace, monospace",
                    lineNumbers: "on",
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    tabSize: 2,
                    insertSpaces: true,
                    renderWhitespace: "selection",
                    smoothScrolling: !reduced,
                    cursorBlinking: reduced ? "solid" : "smooth",
                    cursorSmoothCaretAnimation: reduced ? "off" : "on",
                    padding: { top: 12, bottom: 12 },
                    automaticLayout: true,
                }}
            />
        </div>
    );
}
